import React, { memo, useMemo, useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import moment from "moment";
import { activityLine, groupByDay } from "../utils/activityText";

const FONT = "Inter, Poppins, sans-serif";
const NAVY = "#1E3A5F";
const TONE = {
  success: { color: "#067647", bg: "#ECFDF3", border: "#ABEFC6" },
  error: { color: "#B42318", bg: "#FEF3F2", border: "#FECDCA" },
  warning: { color: "#B54708", bg: "#FFFAEB", border: "#FEDF89" },
  info: { color: "#175CD3", bg: "#EFF8FF", border: "#B2DDFF" },
  navy: { color: NAVY, bg: "#EEF3F9", border: "#C9D7E8" },
  default: { color: "#344054", bg: "#F9FAFB", border: "#EAECF0" },
};

const iconFor = (line) => {
  switch (line.kind) {
    case "created":
    case "saved-held":
      return <AddRoundedIcon />;
    case "edited":
      return <EditNoteRoundedIcon />;
    case "retried":
    case "resubmitted":
    case "stage-run":
    case "kyc-files-run":
      return <ReplayRoundedIcon />;
    case "held":
      return <PauseRoundedIcon />;
    case "released":
    case "started":
      return <PlayArrowRoundedIcon />;
    case "stages-by-hand":
    case "stages-done-by-hand":
    case "stage-reset":
    case "stages-cleared":
      return <TuneRoundedIcon />;
    case "kyc-files":
      return <BadgeOutlinedIcon />;
    case "brisk-manual":
      return <UploadFileRoundedIcon />;
    case "part-result":
      return line.tone === "success" ? <CheckRoundedIcon /> : line.tone === "error" ? <CloseRoundedIcon /> : line.tone === "warning" ? <AutorenewRoundedIcon /> : <SmartToyOutlinedIcon />;
    default:
      return <AutorenewRoundedIcon />;
  }
};

/** One entry: icon, "Who did what", one more line, time; "Details" shows the stored record. */
const Row = memo(({ entry, last }) => {
  const [open, setOpen] = useState(false);
  const line = activityLine(entry);
  const tone = TONE[line.tone] || TONE.default;
  return (
    <Box data-testid="activity-row" sx={{ display: "flex", gap: 1.25, position: "relative", pb: last ? 0 : 1.5 }}>
      {!last && <Box sx={{ position: "absolute", left: 14, top: 30, bottom: 0, width: "2px", backgroundColor: "#EAECF0" }} />}
      <Box
        sx={{
          width: 30,
          height: 30,
          flexShrink: 0,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: tone.color,
          backgroundColor: tone.bg,
          border: `1px solid ${tone.border}`,
          "& svg": { fontSize: 17 },
        }}
      >
        {iconFor(line)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
        <Typography sx={{ fontSize: "13px", color: "#101828", fontFamily: FONT, lineHeight: 1.45 }}>
          {line.who ? <strong>{line.who} </strong> : null}
          {line.text}
        </Typography>
        {line.detail ? (
          <Typography sx={{ fontSize: "12px", color: "#475467", fontFamily: FONT, mt: 0.15, overflowWrap: "anywhere" }}>{line.detail}</Typography>
        ) : null}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: "11.5px", color: "#667085", fontFamily: FONT }}>{entry.timestamp ? moment(entry.timestamp).format("hh:mm:ss A") : "—"}</Typography>
          {line.badge && (
            <Chip label={line.badge} size="small" sx={{ height: 18, fontSize: "10.5px", fontWeight: 600, color: "#B54708", backgroundColor: "#FEF0C7" }} />
          )}
          <Typography
            component="button"
            type="button"
            onClick={() => setOpen((v) => !v)}
            sx={{ border: 0, background: "none", p: 0, cursor: "pointer", fontSize: "11.5px", color: "#667085", textDecoration: "underline", fontFamily: FONT }}
          >
            {open ? "Hide details" : "Details"}
          </Typography>
        </Box>
        {open && (
          <Typography data-testid="activity-raw" sx={{ mt: 0.5, fontSize: "11.5px", color: "#475467", fontFamily: "monospace", backgroundColor: "#F9FAFB", border: "1px solid #EAECF0", borderRadius: "6px", px: 1, py: 0.5, overflowWrap: "anywhere" }}>
            {[entry.from || "—", "→", entry.to || "—", entry.part ? `· part ${entry.part}` : "", entry.note ? `· ${entry.note}` : ""].filter(Boolean).join(" ")}
          </Typography>
        )}
      </Box>
    </Box>
  );
});
Row.displayName = "ActivityRow";

/**
 * The policy's Activity (2026-10-22): everything that happened, newest first, grouped by day, in plain words
 * (utils/activityText.js). The newest PAGE_SIZE show; "Show older" adds more.
 */
const ActivityList = ({ entries = [], pageSize = 25, now }) => {
  const [shown, setShown] = useState(pageSize);
  const groups = useMemo(() => groupByDay(entries, now), [entries, now]);
  if (!entries.length) {
    return <Typography sx={{ fontSize: "12.5px", color: "#667085", fontFamily: FONT }}>Nothing has happened yet - the steps of the policy appear here as they happen.</Typography>;
  }
  let left = shown;
  const visible = [];
  for (const g of groups) {
    if (left <= 0) break;
    const take = g.entries.slice(0, left);
    left -= take.length;
    visible.push({ day: g.day, entries: take });
  }
  const hidden = entries.length - Math.min(shown, entries.length);
  return (
    <Box>
      {visible.map((g) => (
        <Box key={g.day} sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: "#667085", textTransform: "uppercase", letterSpacing: "0.04em", fontFamily: FONT, mb: 1 }}>{g.day}</Typography>
          {g.entries.map((e, i) => (
            <Row key={`${e.timestamp}-${i}`} entry={e} last={i === g.entries.length - 1} />
          ))}
        </Box>
      ))}
      {hidden > 0 && (
        <Button size="small" onClick={() => setShown((n) => n + pageSize)} sx={{ textTransform: "none", fontWeight: 600, color: NAVY }}>
          Show older ({hidden})
        </Button>
      )}
    </Box>
  );
};

export default memo(ActivityList);
